/**
 * Router Auth Connector
 *
 * Connects the router to the auth state using the DI system.
 * This module solves the ES module deadlock problem where router.ts
 * cannot access DI at module evaluation time.
 *
 * Instead of having the router depend on DI at the module level,
 * we use this connector to wire up auth reactivity AFTER DI initialization.
 */

import { resolve } from "../di";
import type { IAuthState, IAuthService } from "../services/auth/types";
import type { WowRouter, RouteConfig } from "../../ui-components/router/wow-router";

/**
 * User type that matches the router's currentUser property
 */
type RouterUser = { id: string; name: string; roles: [string] };

/**
 * Options for the router-auth connector
 */
export interface RouterAuthConnectorOptions {
  /**
   * Callback when auth state is ready (initial check complete)
   */
  onReady?: () => void;

  /**
   * Callback when user changes (sign-in, sign-out, or user data update)
   * @param user - The new current user (undefined if signed out)
   * @param previousUserId - The ID of the previous user (undefined if was signed out)
   */
  onUserChange?: (user: RouterUser | undefined, previousUserId: string | undefined) => void;

  /**
   * Route to navigate to after sign-in (if currently on sign-in page)
   */
  postSignInRoute?: string;

  /**
   * The sign-in route name (defaults to "sign-in")
   */
  signInRoute?: string;

  /**
   * Optional: Role required for post-sign-in navigation
   * If specified, will only navigate if user has this role
   */
  requiredRoleForPostSignIn?: string;
}

/**
 * Connect a router to the DI-based auth state.
 *
 * This function:
 * 1. Sets the router's initial currentUser from auth state
 * 2. Registers synchronous callbacks for sign-in/sign-out to update router
 * 3. Optionally handles post-sign-in navigation
 *
 * IMPORTANT: Uses synchronous callbacks (not effects) to ensure router.currentUser
 * is set BEFORE any navigation happens in the sign-in page.
 *
 * @param router - The WowRouter instance to connect
 * @param navigateTo - The navigation function from the router module
 * @param options - Configuration options
 * @returns A cleanup function to stop watching
 *
 * @example
 * ```typescript
 * // In app.ts after initializeDi()
 * import { router, navigateTo } from "./router";
 * import { connectRouterToAuth, waitForAuthReady } from "ui-common/router/router-auth-connector";
 *
 * initializeDi();
 *
 * // Connect router to auth state
 * connectRouterToAuth(router, navigateTo, {
 *   postSignInRoute: "profile",
 *   signInRoute: "sign-in",
 *   onUserChange: (user, prevId) => {
 *     // App-specific logic (e.g., invalidate profile query)
 *   },
 * });
 *
 * // Wait for initial auth check
 * await waitForAuthReady();
 * ```
 */
export function connectRouterToAuth<T extends Record<string, RouteConfig>>(
  router: WowRouter<T, string>,
  navigateTo: (route: keyof T, updateHistory?: boolean, additionalPathSegments?: string[]) => Promise<boolean>,
  options: RouterAuthConnectorOptions = {},
): () => void {
  const authState = resolve<IAuthState>("authState");
  const authService = resolve<IAuthService>("authService");

  /**
   * Get the current user in the format the router expects
   */
  function getCurrentUser(): RouterUser | undefined {
    const user = authState.currentUser.get();
    if (!user) return undefined;
    return { id: user.id, name: user.name, roles: user.roles as [string] };
  }

  // Set initial user
  router.currentUser = getCurrentUser();

  // Track previous user for change detection
  let previousUserId: string | undefined = getCurrentUser()?.id;

  /**
   * Handle user sign-in - runs SYNCHRONOUSLY when auth state changes.
   * This is critical: router.currentUser must be set before the sign-in page
   * attempts to navigate to a private route.
   */
  const unsubscribeSignIn = authService.onSignIn((user) => {
    const prevId = previousUserId;
    previousUserId = user.id;

    // Update router's current user SYNCHRONOUSLY
    router.currentUser = { id: user.id, name: user.name, roles: user.roles as [string] };

    // Force router to re-render with new user context
    // This is necessary because currentUser is not a reactive property
    router.requestUpdate();

    // Call the onUserChange callback if provided
    options.onUserChange?.(router.currentUser, prevId);

    // If this is a session restore (page load, not form sign-in), we need to navigate
    // to the originally requested route. The router stores this when a private route
    // was requested before auth was ready.
    const signInRoute = options.signInRoute ?? "sign-in";
    if (prevId === undefined) {
      // Check if there's a pending auto-checkout (set by sign-in page).
      // If so, skip navigation - the sign-in page will handle it after checkout completes.
      const pendingAutoCheckout = sessionStorage.getItem("pendingAutoCheckout");
      if (pendingAutoCheckout) {
        // Don't navigate - sign-in page will handle navigation after checkout
        return;
      }

      // Check if there was an originally requested route (private route before auth)
      const originalRoute = (router as unknown as { originalRequestedRoute?: keyof T }).originalRequestedRoute;
      const originalSegments =
        (router as unknown as { originalRequestedSegments?: string[] }).originalRequestedSegments ?? [];

      if (originalRoute && originalRoute !== signInRoute) {
        // Navigate to the originally requested route with preserved path segments
        navigateTo(originalRoute, true, originalSegments);
        // Clear the original route to prevent re-navigation
        (router as unknown as { originalRequestedRoute?: keyof T }).originalRequestedRoute = undefined;
        (router as unknown as { originalRequestedSegments?: string[] }).originalRequestedSegments = [];
      } else if (options.postSignInRoute && router.currentRoute === signInRoute) {
        // If we were on sign-in page, go to the post-sign-in route
        navigateTo(options.postSignInRoute as keyof T);
      }
    }
  });

  /**
   * Handle user sign-out - runs SYNCHRONOUSLY when auth state changes.
   */
  const unsubscribeSignOut = authService.onSignOut(() => {
    const prevId = previousUserId;
    previousUserId = undefined;

    // Clear router's current user
    router.currentUser = undefined;

    // Call the onUserChange callback if provided
    options.onUserChange?.(undefined, prevId);

    // Navigate to sign-in page if we're on a private route
    const signInRoute = options.signInRoute ?? "sign-in";
    const currentRouteConfig = (router as unknown as { config: Record<string, { isPrivate?: boolean }> }).config[
      String(router.currentRoute)
    ];

    if (currentRouteConfig?.isPrivate) {
      // User was on a private route, navigate to sign-in
      navigateTo(signInRoute as keyof T);
    } else {
      // Just re-render the current page
      router.requestUpdate();
    }
  });

  // Set up onReady callback if provided
  if (options.onReady) {
    authState.ready.then(options.onReady);
  }

  // Return cleanup function
  return () => {
    unsubscribeSignIn();
    unsubscribeSignOut();
  };
}

/**
 * Wait for the auth state to be ready (initial session check complete).
 *
 * Call this after connectRouterToAuth to ensure the initial render
 * respects the user's authenticated state.
 *
 * @returns Promise that resolves when auth is ready
 */
export async function waitForAuthReady(): Promise<void> {
  const authState = resolve<IAuthState>("authState");
  await authState.ready;
}
